var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { assertUserTypeOptional } from "healthex-common/checkusertype";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { autorun, computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { FormModel } from "./FormModel";
import { NamedRoutes } from "./NamedRoutes";
import { isErr } from "healthex-common/result";
export class WelcomeUserDetailsViewModel {
    constructor(container) {
        this.error = null;
        this.orgUser = NOT_LOADED;
        this.organization = NOT_LOADED;
        this.formModel = new FormModel({
            firstName: "",
            lastName: "",
            title: "",
            providerId: "",
        }, ["firstName", "lastName"]);
        this.authManager = container.authManager;
        this.routingUIManager = container.routingUIManager;
        this.organizationsManager = container.organizationsManager;
        this.welcomeUserUIManager = container.welcomeUserUIManager;
        autorun(async () => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            this.orgUser = await this.loadHealthcareOrganizationUser();
            this.organization = await this.loadHealthcareOrganization();
            this.formModel.updateInput({
                firstName: (_b = (_a = this.orgUser) === null || _a === void 0 ? void 0 : _a.firstName) !== null && _b !== void 0 ? _b : "",
                lastName: (_d = (_c = this.orgUser) === null || _c === void 0 ? void 0 : _c.lastName) !== null && _d !== void 0 ? _d : "",
                title: (_f = (_e = this.orgUser) === null || _e === void 0 ? void 0 : _e.title) !== null && _f !== void 0 ? _f : "",
                providerId: (_h = (_g = this.orgUser) === null || _g === void 0 ? void 0 : _g.providerId) !== null && _h !== void 0 ? _h : "",
            });
        });
    }
    async loadHealthcareOrganizationUser() {
        if (!this.authManager.orgId || !this.authManager.id)
            return null;
        try {
            return await this.organizationsManager.loadUserByOrganizationAndId(this.authManager.orgId, this.authManager.id);
        }
        catch (e) {
            return null;
        }
    }
    async loadHealthcareOrganization() {
        if (!this.authManager.orgId)
            return null;
        try {
            return assertUserTypeOptional(await this.organizationsManager.loadOrganization(this.authManager.orgId), UserType.HEALTHCARE_ORGANIZATION);
        }
        catch (e) {
            return null;
        }
    }
    get isFormInvalid() {
        const input = this.formModel.input;
        return input.firstName === "" || input.lastName === "" || (this.isProvider && input.providerId === "");
    }
    get buttonText() {
        return this.isOrgAdmin && !this.isOrgSetupComplete ? "Continue" : "Done";
    }
    get showProgressBar() {
        var _a;
        if (!this.routingUIManager.currentRoute)
            return false;
        if (((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) === NamedRoutes.WELCOME_ORG_USER_DETAILS.routeName &&
            (!this.isOrgAdmin || this.isOrgSetupComplete))
            return false;
        return true;
    }
    get progressBarPercentage() {
        return this.welcomeUserUIManager.progressBarPercentage;
    }
    get isOrgSetupComplete() {
        if (this.organization === NOT_LOADED || this.organization === null)
            return false;
        return this.organization.setupComplete;
    }
    get isOrgAdmin() {
        return this.authManager.isOrgAdmin;
    }
    get isProvider() {
        if (this.orgUser === NOT_LOADED || !this.orgUser)
            return false;
        return this.orgUser.permissions.includes(UserPermissions.PROVIDER);
    }
    get showProviderIdTextField() {
        return this.isProvider;
    }
    async save() {
        if (this.orgUser === NOT_LOADED || this.orgUser === null)
            return;
        const { firstName, lastName, title, providerId } = this.formModel.input;
        const update = { ...{ firstName, lastName, title }, ...(this.isProvider ? { providerId } : {}) };
        if ((this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION ||
            this.authManager.userType === UserType.EXTERNAL_RESEARCHER) &&
            this.authManager.orgId &&
            this.orgUser.id) {
            try {
                const result = await this.organizationsManager.editUser(this.authManager.orgId, this.orgUser.id, {
                    type: this.authManager.userType,
                    ...update,
                });
                if (isErr(result)) {
                    switch (result.error) {
                        case "PROVIDER_ID_ALREADY_TAKEN":
                            this.formModel.addError("providerId", "Enter a unique provider ID");
                            return;
                    }
                }
                await this.authManager.refreshTokenInfo();
                if (this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION) {
                    this.routingUIManager.navigateTo(this.isOrgAdmin && !this.isOrgSetupComplete
                        ? NamedRoutes.WELCOME_BASIC_ORG_DETAILS
                        : NamedRoutes.HEALTHCARE_ORG_ANALYTICS);
                }
                else {
                    this.routingUIManager.navigateTo(this.isOrgAdmin && !this.isOrgSetupComplete ? NamedRoutes.WELCOME_BASIC_ORG_DETAILS : NamedRoutes.PROJECTS);
                }
            }
            catch (e) {
                if (e instanceof Error) {
                    this.error = e.message;
                }
                else {
                    this.error = "An error occurred";
                }
            }
        }
    }
}
__decorate([
    observable
], WelcomeUserDetailsViewModel.prototype, "error", void 0);
__decorate([
    observable
], WelcomeUserDetailsViewModel.prototype, "orgUser", void 0);
__decorate([
    observable
], WelcomeUserDetailsViewModel.prototype, "organization", void 0);
__decorate([
    computed
], WelcomeUserDetailsViewModel.prototype, "isFormInvalid", null);
__decorate([
    computed
], WelcomeUserDetailsViewModel.prototype, "buttonText", null);
__decorate([
    computed
], WelcomeUserDetailsViewModel.prototype, "showProgressBar", null);
__decorate([
    computed
], WelcomeUserDetailsViewModel.prototype, "progressBarPercentage", null);
__decorate([
    computed
], WelcomeUserDetailsViewModel.prototype, "isOrgSetupComplete", null);
__decorate([
    computed
], WelcomeUserDetailsViewModel.prototype, "isOrgAdmin", null);
__decorate([
    computed
], WelcomeUserDetailsViewModel.prototype, "isProvider", null);
__decorate([
    computed
], WelcomeUserDetailsViewModel.prototype, "showProviderIdTextField", null);
